import React from 'react';
import { connect } from 'react-redux';
import Modal from '@roo/roo/Modal';

import { RootState } from '@projects/Shell/store';
import * as LX from '@utils/lxWrapper';
import { getStorage, setStorage } from '@utils/localStorage';
import { getMessageConfig } from '@utils/messageConfig';
import { isHeadMultiPoi } from '@utils/ids';
import { OrderNotification } from '../../store/models/pushNotification';
import { CustomizedShowArrType } from '../../api/define';

import styles from './OrderNotificationBox.module.scss';

const TIPS_COUNT_STORAGE_KEY = 'waimaie_order_tips_count';

const { useCallback, useState, useEffect } = React;

const OrderNotificationBox: React.FC<{
    notification: OrderNotification | null;
    shouldShowNotification: boolean;
}> = ({ notification, shouldShowNotification }) => {
    /** 当前消息已读状态 */
    const [isNotificationRead, setNotificationReadStatus] = useState(false);
    /** 是否下次刷新页面前不再展示订单通知 */
    const [
        hideOrderNotificationUntilPageRefresh,
        changeHideOrderNotificationUntilPageRefreshState,
    ] = useState(false);

    /** 是否展示订单通知，可通过白名单或者消息提醒设置页配置 */
    const [messageConfig, setMessageConfig] = useState(true);

    /** 获取订单push配置 */
    useEffect(() => {
        setMessageConfig(getMessageConfig().orderNotification !== false);
    }, []);

    /** 已读消息处理 */
    const readNotification = useCallback(() => {
        setNotificationReadStatus(true);
        const readReceiptConfig = notification && notification.readReceiptConfig;
        if (readReceiptConfig) {
            LX.reportMC(
                readReceiptConfig.mc,
                readReceiptConfig.cid,
                readReceiptConfig.custom || {},
            );
        }
    }, [setNotificationReadStatus, notification]);

    /** 消息体点击处理 */
    const handleContentClick = useCallback(() => {
        if (notification && notification.href) {
            window.location.hash = notification.href;
            readNotification();
        }
    }, [notification && notification.href]);

    const noMoreNotification = useCallback(() => {
        const currentTipsCount = Number(getStorage(TIPS_COUNT_STORAGE_KEY) || '0');
        if (currentTipsCount < 10) {
            Modal.confirm({
                title: null,
                children: '确认后，直到下次刷新页面之前，订单消息不会再次展示',
                onConfirm: () => {
                    changeHideOrderNotificationUntilPageRefreshState(true);
                    setStorage(TIPS_COUNT_STORAGE_KEY, `${currentTipsCount + 1}`);
                },
            });
        } else {
            changeHideOrderNotificationUntilPageRefreshState(true);
        }
    }, [changeHideOrderNotificationUntilPageRefreshState]);

    useEffect(() => {
        // 收到新消息，重置已读状态
        setNotificationReadStatus(false);
    }, [notification, setNotificationReadStatus]);

    if (notification === null || isNotificationRead || hideOrderNotificationUntilPageRefresh) {
        return null;
    }

    if (!shouldShowNotification || (isHeadMultiPoi() && !messageConfig)) {
        return null;
    }

    return (
        <div className={styles.orderNotification}>
            <div className={styles.content} onClick={handleContentClick}>
                {notification.content}
                {notification.wmPoiName ? (
                    <span className={styles.poiName}>{notification.wmPoiName}</span>
                ) : null}
            </div>
            <div className={styles.operation}>
                <span onClick={noMoreNotification}>不再提示</span>
                <span onClick={readNotification}>我知道了</span>
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        shouldShowNotification: !state.shellCommon.customizedShowArr.includes(
            CustomizedShowArrType.PUSH_MESSAGE,
        ),
        notification: state.pushNotification.orderNotification,
    };
};

export default connect(mapStateToProps)(OrderNotificationBox);
